import React from 'react'
import { Heading, Text } from '@primer/react-brand'

import StripeCardForm from '../components/StripeCardForm'
import CurrentCardPanel from '../components/CurrentCardPanel'
import { API_BASE_URL } from '../../config/apiConfig'

// UC-9 — Update payment method. Admin-only in-app screen to change the card on
// file, reached from the sidebar "Payment declined" card. Renders content-only
// into the header-only app chrome (civic_lens_subscribe layout). Shows the saved
// card (CurrentCardPanel) above the shared StripeCardForm, which captures a new
// card and hands it to POST /api/v2/organizations/update_payment_method — the
// backend repoints the default card and settles any outstanding balance. On
// success it lands on the payment-method-updated screen.
//
// Props: { stripePublishableKey, currentCard: { brand, last4, expMonth, expYear } | null, returnTo }

const csrfToken = () => document.querySelector('meta[name="csrf-token"]')?.content

// Where Cancel returns to. The self-serve account page is the natural home until
// a dedicated entry point is wired.
const CANCEL_PATH = '/users/account'
// Where a successful save lands (the confirmation screen).
const SUCCESS_PATH = '/account/payment-method/updated'
// Handoff key for the transient charge summary read by the success screen.
const CHARGE_STASH_KEY = 'cardUpdateCharge'

const UpdatePaymentMethodPage = ({ stripePublishableKey, currentCard }) => {
  // Swap the saved card via the update endpoint; the backend settles any balance
  // (approved-but-unpaid → charge now, past_due → pay the open invoice) and
  // returns a charge summary. Stash it for the success screen, then navigate.
  // Returns an error string for the form to display, or navigates on success.
  const handleConfirmed = async (setupIntentId) => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/v2/organizations/update_payment_method`, {
        method: 'POST',
        credentials: 'same-origin',
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
          'X-CSRF-Token': csrfToken(),
        },
        body: JSON.stringify({ setup_intent_id: setupIntentId }),
      })
      const data = await response.json().catch(() => ({}))

      if (response.ok && data.success) {
        try { sessionStorage.setItem(CHARGE_STASH_KEY, JSON.stringify(data.charge || null)) } catch (e) { /* non-fatal */ }
        window.location.href = SUCCESS_PATH
        return null
      }

      const first = Array.isArray(data.errors) ? data.errors[0] : null
      return (typeof first === 'string' && first) || first?.detail ||
        'Something went wrong saving your card. Please try again.'
    } catch (err) {
      return 'Something went wrong saving your card. Please try again.'
    }
  }

  return (
    <div style={{ maxWidth: 480, width: '100%', margin: '0 auto', padding: '52px 40px 96px', fontFamily: 'var(--brand-body-fontFamily)' }}>
      <Heading as="h1" size="5">Payment details</Heading>
      <Text as="p" size="300" variant="muted" style={{ marginTop: 6, marginBottom: 28, lineHeight: 1.65 }}>
        {/* Worded to be true for every org that can reach this page — paying, a
            failed payment, and (since 2026-09-18) a pending application, which has
            no billing cycle yet and is charged at approval (Mischa, 2026-09-18). */}
        Update the card we keep on file. If a payment has failed, this card is charged right
        away. Otherwise nothing is charged now. We&apos;ll use this card for your next payment.
      </Text>

      <CurrentCardPanel card={currentCard} />

      <StripeCardForm
        stripePublishableKey={stripePublishableKey}
        submitLabel="Save card"
        secondaryAction={{ label: 'Cancel', onClick: () => { window.location.href = CANCEL_PATH } }}
        onConfirmed={handleConfirmed}
      />
    </div>
  )
}

export default UpdatePaymentMethodPage
